import { supabase } from './supabase'
import { sendInvitationEmail } from './email'

/**
 * Create an organization invitation
 */
export const createInvitation = async (organizationId, email, role = 'member', permissions = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    // Check if a pending invitation already exists for this email
    const { data: existingInvitation } = await supabase
      .from('organization_invitations')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('email', email.toLowerCase().trim())
      .eq('status', 'pending')
      .maybeSingle()

    if (existingInvitation) {
      // Return existing invitation instead of creating duplicate
      return {
        ...existingInvitation,
        isExisting: true,
      }
    }
    
    // Get organization name
    const { data: orgData } = await supabase
      .from('organizations')
      .select('name')
      .eq('id', organizationId)
      .single()
    
    const { data, error } = await supabase
      .from('organization_invitations')
      .insert({
        organization_id: organizationId,
        email: email.toLowerCase().trim(),
        role,
        permissions,
        invited_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      // Handle duplicate key error specifically
      if (error.code === '23505' || error.message.includes('duplicate key') || error.message.includes('unique constraint')) {
        // Try to get the existing invitation
        const { data: existing } = await supabase
          .from('organization_invitations')
          .select('*')
          .eq('organization_id', organizationId)
          .eq('email', email.toLowerCase().trim())
          .eq('status', 'pending')
          .maybeSingle()
        
        if (existing) {
          return {
            ...existing,
            isExisting: true,
          }
        }
        throw new Error('An invitation already exists for this email address.')
      }
      throw error
    }

    // Send invitation email
    let emailSent = false
    let emailError = null
    try {
      await sendInvitationEmail(email, {
        token: data.token,
        organizationName: orgData?.name || 'Organization',
        role,
        organizationId, // Pass organizationId for dynamic "from" email
      })
      emailSent = true
    } catch (err) {
      emailError = err
      console.error('Failed to send invitation email:', err)
      // Log detailed error for debugging
      console.error('Email error details:', {
        message: err.message,
        error: err,
        email,
        organizationName: orgData?.name,
      })
      // Don't throw - return the invitation with email status
    }

    return {
      ...data,
      emailSent,
      emailError: emailError ? {
        message: emailError.message,
        details: emailError.details || emailError,
      } : null,
    }
  } catch (error) {
    console.error('Error creating invitation:', error)
    throw error
  }
}

/**
 * Get invitation by token
 */
export const getInvitationByToken = async (token) => {
  try {
    const { data, error } = await supabase
      .from('organization_invitations')
      .select('*, organizations(*)')
      .eq('token', token)
      .eq('status', 'pending')
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error getting invitation:', error)
    throw error
  }
}

import { sendWelcomeEmail } from './email'

/**
 * Accept invitation
 */
export const acceptInvitation = async (token) => {
  try {
    // First, get invitation details before accepting (for welcome email)
    let invitationData = null
    try {
      const { data: invitation } = await getInvitationByToken(token)
      invitationData = invitation
    } catch (err) {
      // If we can't get invitation details, continue anyway
      console.warn('Could not fetch invitation details before acceptance:', err)
    }

    // Accept the invitation
    const { data, error } = await supabase.rpc('accept_invitation', {
      invitation_token: token,
    })

    if (error) throw error

    // Send welcome email if we have the invitation data
    if (invitationData || data?.success) {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        const userName = user?.user_metadata?.name || user?.email || 'User'
        
        // Use invitation data if available, otherwise try to get org name from the result
        const orgName = invitationData?.organizations?.name || 'Organization'
        const orgId = invitationData?.organization_id

        await sendWelcomeEmail(user?.email, {
          userName,
          organizationName: orgName,
          organizationId: orgId,
        })
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError)
        // Don't fail invitation acceptance if email fails
      }
    }

    return data
  } catch (error) {
    // Handle abort errors gracefully
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      console.error('Request was aborted. This may be due to navigation or timeout.')
      throw new Error('The request was cancelled. Please try again.')
    }
    console.error('Error accepting invitation:', error)
    throw error
  }
}

/**
 * Reject invitation
 */
export const rejectInvitation = async (token) => {
  try {
    const { data, error } = await supabase.rpc('reject_invitation', {
      invitation_token: token,
    })

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error rejecting invitation:', error)
    throw error
  }
}

/**
 * Get pending invitations for current user
 */
export const getMyPendingInvitations = async () => {
  try {
    const { data, error } = await supabase.rpc('get_my_pending_invitations')

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error getting pending invitations:', error)
    throw error
  }
}

/**
 * Get all invitations for an organization (all statuses)
 */
export const getOrganizationInvitations = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('organization_invitations')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error getting invitations:', error)
    throw error
  }
}

/**
 * Cancel invitation
 */
export const cancelInvitation = async (invitationId) => {
  try {
    const { error } = await supabase
      .from('organization_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error cancelling invitation:', error)
    throw error
  }
}

/**
 * Delete invitation permanently
 */
export const deleteInvitation = async (invitationId) => {
  try {
    const { error } = await supabase
      .from('organization_invitations')
      .delete()
      .eq('id', invitationId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error deleting invitation:', error)
    throw error
  }
}

/**
 * Create join request
 */
export const createJoinRequest = async (organizationId, requestedRole = 'member', message = '') => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    const { data, error } = await supabase
      .from('organization_join_requests')
      .insert({
        organization_id: organizationId,
        user_id: user?.id,
        requested_role: requestedRole,
        message,
      })
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error creating join request:', error)
    throw error
  }
}

/**
 * Get join requests for an organization
 */
export const getOrganizationJoinRequests = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('organization_join_requests')
      .select('*, users:user_id(email, raw_user_meta_data)')
      .eq('organization_id', organizationId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error getting join requests:', error)
    throw error
  }
}

/**
 * Approve join request
 */
export const approveJoinRequest = async (requestId, role, permissions = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    // Get request
    const { data: request, error: fetchError } = await supabase
      .from('organization_join_requests')
      .select('*')
      .eq('id', requestId)
      .single()

    if (fetchError) throw fetchError

    // Add user to organization
    const { error: memberError } = await supabase
      .from('organization_members')
      .insert({
        organization_id: request.organization_id,
        user_id: request.user_id,
        role: role || request.requested_role,
        permissions,
      })

    if (memberError) throw memberError

    // Update request status
    const { error: updateError } = await supabase
      .from('organization_join_requests')
      .update({
        status: 'approved',
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', requestId)

    if (updateError) throw updateError
    return true
  } catch (error) {
    console.error('Error approving join request:', error)
    throw error
  }
}

/**
 * Reject join request
 */
export const rejectJoinRequest = async (requestId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    const { error } = await supabase
      .from('organization_join_requests')
      .update({
        status: 'rejected',
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', requestId)

    if (error) throw error
    return true
  } catch (error) {
    console.error('Error rejecting join request:', error)
    throw error
  }
}

/**
 * Get signup token (for master admin)
 */
export const getSignupToken = async (token) => {
  try {
    const { data, error } = await supabase
      .from('organization_signup_tokens')
      .select('*')
      .eq('token', token)
      .eq('status', 'pending')
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error getting signup token:', error)
    throw error
  }
}

/**
 * Use signup token (during registration)
 */
export const useSignupToken = async (token, userName, userEmail) => {
  try {
    const { data, error } = await supabase.rpc('use_signup_token', {
      token_param: token,
      user_name: userName,
      user_email: userEmail,
    })

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error using signup token:', error)
    throw error
  }
}

import { sendSignupTokenEmail } from './email'

/**
 * Create signup token (master admin only)
 */
export const createSignupToken = async (organizationName, adminEmail, adminName) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    const { data, error } = await supabase
      .from('organization_signup_tokens')
      .insert({
        organization_name: organizationName,
        admin_email: adminEmail,
        admin_name: adminName,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) throw error

    // Send signup token email
    try {
      await sendSignupTokenEmail(adminEmail, {
        token: data.token,
        organizationName,
        adminName,
      })
    } catch (emailError) {
      console.error('Failed to send signup token email:', emailError)
      // Don't fail token creation if email fails
    }

    return data
  } catch (error) {
    console.error('Error creating signup token:', error)
    throw error
  }
}

